import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Trash2, Plus } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { updateChangeOrder } from '@/services/changeOrderService';
import { useToast } from '@/hooks/use-toast';

export const EditChangeOrderDialog = ({ open, onOpenChange, changeOrder, project, customer, onSaveSuccess }) => {
  const { toast } = useToast();
  const sigCanvas = useRef(null);

  // Form state
  const [lineItems, setLineItems] = useState([
    { description: '', price: 0, quantity: 1 }
  ]);
  const [loading, setLoading] = useState(false);

  // Calculated values
  const [changeOrderTotal, setChangeOrderTotal] = useState(0);
  const [previousTotalJobCost, setPreviousTotalJobCost] = useState(0);
  const [newProjectTotal, setNewProjectTotal] = useState(0);

  // Initialize values when dialog opens with existing change order data
  useEffect(() => {
    if (open && changeOrder && project) {
      // Pre-populate line items from existing change order
      if (changeOrder.change_order_data && changeOrder.change_order_data.length > 0) {
        setLineItems(changeOrder.change_order_data.map(item => ({
          description: item.description,
          price: item.price,
          quantity: item.quantity
        })));
      }
      
      // Set previous totals
      const currentJobCost = project.total_job_cost || project.contract_price || 0;
      setPreviousTotalJobCost(currentJobCost);
      
      // If there's a signature, load it
      if (changeOrder.signature && sigCanvas.current) {
        setTimeout(() => {
          sigCanvas.current?.fromDataURL(changeOrder.signature);
        }, 100);
      }
    }
  }, [open, changeOrder, project]);

  // Calculate totals whenever line items change
  useEffect(() => {
    calculateTotals(lineItems, previousTotalJobCost);
  }, [lineItems, previousTotalJobCost]);

  const calculateTotals = (items, previousCost) => {
    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0));
    }, 0);
    
    setChangeOrderTotal(total);
    setNewProjectTotal(previousCost + total);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', price: 0, quantity: 1 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      const newItems = lineItems.filter((_, i) => i !== index);
      setLineItems(newItems);
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    setLineItems(newItems);
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleSave = async () => {
    // Validation
    if (lineItems.some(item => !item.description.trim())) {
      toast({
        title: 'Validation Error',
        description: 'All line items must have a description.',
        variant: 'destructive',
      });
      return;
    }

    if (changeOrderTotal === 0) {
      toast({
        title: 'Validation Error',
        description: 'Change order total cannot be zero.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Get signature if present
      let signatureData = null;
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        signatureData = sigCanvas.current.toDataURL();
      } else if (changeOrder.signature) {
        // Keep existing signature if canvas is empty
        signatureData = changeOrder.signature;
      }

      // Prepare update data
      const updateData = {
        change_order_data: lineItems,
        total: changeOrderTotal,
        previous_total_job_cost: previousTotalJobCost,
        new_project_total: newProjectTotal,
        status: signatureData ? 'Signed' : 'Unsigned',
        signature: signatureData
      };

      // Update the change order
      await updateChangeOrder(changeOrder.id, updateData);

      toast({
        title: 'Success',
        description: 'Change order updated successfully.',
      });

      // Call success callback to refresh data
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating change order:', error);
      toast({
        title: 'Error',
        description: error?.detail || 'Failed to update change order.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!changeOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Change Order</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Info Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Customer</Label>
                <p className="text-sm">{customer?.name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Project Address</Label>
                <p className="text-sm">{project?.jobsite_address || project?.address || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <Label className="text-lg font-semibold">Scope of Work</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLineItem}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <Card key={index}>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-6">
                        <Label htmlFor={`description-${index}`}>Description</Label>
                        <Input
                          id={`description-${index}`}
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Enter description"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor={`quantity-${index}`}>Quantity</Label>
                        <Input
                          id={`quantity-${index}`}
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label htmlFor={`price-${index}`}>Unit Price</Label>
                        <Input
                          id={`price-${index}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateLineItem(index, 'price', parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLineItem(index)}
                          disabled={lineItems.length === 1}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-right">
                      <span className="text-sm font-semibold">
                        Total: {formatCurrency(item.price * item.quantity)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Cost Summary */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="font-semibold">Change Order Total:</span>
              <span className="font-bold text-blue-600">{formatCurrency(changeOrderTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Previous Total Job Cost:</span>
              <span>{formatCurrency(previousTotalJobCost)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between">
              <span className="font-bold">New Project Total:</span>
              <span className="font-bold text-green-600">
                {formatCurrency(newProjectTotal)}
              </span>
            </div>
          </div>

          {/* Signature Section */}
          <div>
            <Label className="text-lg font-semibold mb-2">Customer Signature</Label>
            <div className="border rounded-lg p-4 bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: 'signature-canvas w-full h-32 border rounded',
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="mt-2"
              >
                Clear Signature
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {changeOrder.signature && sigCanvas.current?.isEmpty() 
                ? 'Existing signature will be retained if you don\'t sign again.' 
                : 'Sign above to approve this change order.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
